import type { RouterConfig } from "@nuxt/schema";

import type { LocaleMorpheus } from "@solvari/translations";

import {
  buildConstraint,
  buildCustomServerRoutes,
  buildStatamicRoutes,
} from "@/app/routes.helpers.ts";
import {
  fetchZiggy,
  getPageUris,
  getProductOverviewSlugs,
  getProvinceSlugs,
} from "~/app/api/routing.api.ts";
import { toInternalUrl, useHostLocale } from "~/shared/composables/useHost.ts";

// Unmemoized and through the proxy, like every other route source: a memo hit skips the
// `kyCacheTags` hook, so the page it served would carry no ziggy tag at all.
function getZiggy(locale: LocaleMorpheus) {
  return fetchZiggy(toInternalUrl("/cache-proxy/api"), "main-site", locale);
}

/**
 * Server only — registers routes early so SSR has them immediately.
 * The client adds its routes later in 04.router via Pinia Colada,
 * calling the same fetchers.
 */
async function getServerRoutes(locale: LocaleMorpheus) {
  const [
    ziggy,
    constrainedSlugs,
    staticPageUris,
    performanceUris,
    provinceSlugs,
  ] = await Promise.all([
    getZiggy(locale),
    getProductOverviewSlugs(locale),
    getPageUris("staticPage", locale),
    getPageUris("performance", locale),
    getProvinceSlugs(locale),
  ]);

  const overviewConstraint = buildConstraint(constrainedSlugs);
  const provinceConstraint = buildConstraint(provinceSlugs);
  const staticPageConstraint = buildConstraint(staticPageUris);
  const performanceConstraint = buildConstraint(performanceUris);

  return [
    ...buildCustomServerRoutes(ziggy, provinceConstraint),
    ...buildStatamicRoutes(
      {
        overview: overviewConstraint,
        performance: performanceConstraint,
        staticPage: staticPageConstraint,
      },
      locale,
    ),
  ];
}

export default {
  routes: import.meta.client
    ? () => []
    : () => getServerRoutes(useHostLocale()),
} satisfies RouterConfig;
